<template>
  <AdminLayout>
    <Head title="Company Integrations" />

    <div class="item">
      <div class="headline flex flex-row justify-between mx-4 sm:mx-12">
        <h2 class="text-2xl font-semibold text-blue-600 pb-5 pt-5">Integration</h2>
        <div
          @click.prevent="submit()"
          class="relative inline-flex shadow-sm rounded-md self-start mt-2"
        >
          <button
            type="button"
            class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>

      <div class="panel">
        <div
          v-if="$page.props.flash.success"
          class="alert alert-success"
        >
          <p class="alert-text">
            {{ $page.props.flash.success }}
          </p>
        </div>

        <form
          method="POST"
          @submit.prevent="submit()"
        >
          <!-- basic -->
          <div class="grid grid-cols-12 gap-6">
            <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
              <label for="name"> Integration </label>
              <input
                type="text"
                class="input-text"
                disabled
                v-model="form.name"
                name="name"
                id="name"
              />
            </div>
            <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
              <label for="display_name"> Display Name </label>
              <input
                type="text"
                v-model="form.display_name"
                name="type"
                id="display_name"
              />
            </div>
            <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
              <label for="type"> Type </label>
              <input
                type="text"
                class="input-text"
                disabled
                v-model="form.display_type"
                name="type"
                id="type"
              />
            </div>
            <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
              <div class="input-control">
                <MultiSelectInput
                  v-model="locationIds"
                  :options="locationOptions"
                  placeholder="Select Locations"
                >
                  Locations
                </MultiSelectInput>
              </div>
              <p
                v-if="getErrors('locations', form)"
                class="input-error"
              >
                {{ getErrors('locations', form) }}
              </p>
            </div>
            <div class="input-group col-span-12 sm:col-span-6 lg:col-span-4">
              <label for="status"> Status </label>
              <select
                id="status"
                v-model="form.status"
                name="status"
              >
                <option value="">Select status</option>
                <option
                  v-for="(display, key) in integrationStatuses"
                  :value="key"
                >
                  {{ display }}
                </option>
              </select>
              <p
                v-if="getErrors('status', form)"
                class="input-error"
              >
                {{ getErrors('status', form) }}
              </p>
            </div>
          </div>

          <hr class="my-6" />

          <!-- config -->
          <h3>Configuration</h3>

          <div class="grid grid-cols-12 gap-6 mt-4">
            <div
              v-for="(val, key) in integration.data.config"
              :key="key"
              class="input-group col-span-12 sm:col-span-6 lg:col-span-6"
            >
              <template v-if="form.config[key].type === 'boolean'">
                <div class="input-checkbox-container">
                  <input
                    v-model="form.config[key].value"
                    :id="'config_' + key"
                    :name="'scopes[' + key + ']'"
                    type="checkbox"
                  />
                </div>
                <div class="input-label-container">
                  <label :for="'config_' + key">
                    {{ form.config[key].label }}
                  </label>
                </div>
              </template>
              <template v-else>
                <label :for="'config_' + key">
                  {{ form.config[key].label }}
                </label>
                <input
                  type="text"
                  v-model="form.config[key].value"
                  name="type"
                  :id="'config_' + key"
                />
              </template>
            </div>
          </div>

          <hr class="my-6" />

          <!-- location configs -->
          <div v-for="(location, locationKey) in form.locations">
            <h4>{{ location.name }} Configuration</h4>

            <div class="grid grid-cols-12 gap-6 mt-4">
              <div
                v-for="(config, configKey) in location.config"
                :key="locationKey + ':' + configKey"
                class="input-group col-span-12 sm:col-span-6 lg:col-span-6"
              >
                <template v-if="config.type === 'boolean'">
                  <div class="input-checkbox-container">
                    <input
                      v-model="form.locations[locationKey].config[configKey].value"
                      :id="'config_' + configKey"
                      :name="'scopes[' + configKey + ']'"
                      type="checkbox"
                    />
                  </div>
                  <div class="input-label-container">
                    <label :for="'config_' + configKey">
                      {{ config.label }}
                    </label>
                  </div>
                </template>
                <template v-else>
                  <label :for="'config_' + configKey">
                    {{ config.label }}
                  </label>
                  <input
                    type="text"
                    v-model="form.locations[locationKey].config[configKey].value"
                    name="type"
                    :id="'config_' + configKey"
                  />
                </template>
              </div>
            </div>

            <hr class="my-6" />
          </div>

          <!-- scopes -->
          <h3>Scopes</h3>

          <div class="grid grid-cols-12 gap-4 mt-4">
            <div
              v-for="(val, key) in integration.data.scopes"
              :key="key"
              class="input-group-checkbox col-span-12"
            >
              <div class="input-checkbox-container">
                <input
                  v-model="form.scopes[key]"
                  :id="'scope_checkbox_' + key"
                  :name="'scopes[' + key + ']'"
                  type="checkbox"
                />
              </div>
              <div class="input-label-container">
                <label :for="'scope_checkbox_' + key">
                  {{ key }}
                </label>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link, usePage, useForm } from '@inertiajs/vue3'
import checksErrors from '@/Mixins/checksErrors'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput'

export default {
  mixins: [checksErrors],

  components: {
    AdminLayout,
    Head,
    Link,
    MultiSelectInput
  },

  props: {
    integration: Object,
    integrationStatuses: Object,
    locations: Object,
    defaultLocationConfig: Array
  },

  data() {
    return {
      form: {
        display_name: '',
        display_type: '',
        locations: [],
        settings: {},
        config: {},
        scopes: {},
        status: ''
      }
    }
  },

  created() {
    this.form = useForm({
      ...this.form,
      ...this.integration.data
    })
  },

  computed: {
    locationOptions() {
      return _.map(this.locations.data, (location) => {
        return {
          value: location.id,
          label: location.name
        }
      })
    },
    locationIds: {
      get() {
        return _.map(this.form.locations, (location) => {
          return location.location_id
        })
      },
      set(locationIds) {
        this.form.locations = _.map(locationIds, (locationId) => {
          let locationData = _.find(this.form.locations, (location) => location.location_id === locationId)

          if (!locationData) {
            locationData = {
              name: _.find(this.locations.data, (location) => location.id === locationId)?.name || 'NA',
              location_id: locationId,
              config: this.defaultLocationConfig
            }
          }

          return locationData
        })
      }
    }
  },

  methods: {
    submit() {
      this.form.put(this.route('company.integrations.update', { integration: this.integration.data.id }), {
        onFinish: () => {}
      })
    }
  }
}
</script>
